import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { apiErrorCode, apiErrorMessage } from '@/lib/appsumo-upgrade';
import { formatPhone } from '@/lib/format-phone';
import {
  iconDialpad,
  iconMicrophone,
  iconMicrophoneSlash,
  iconPhone,
  iconPhoneHangup,
  iconXmark,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useGetCallOutcomesQuery } from '@/store/features/engage.feature';
import {
  useListUnsavedCallsQuery,
  useWrapUpCallMutation,
  type CallRecord,
} from '@/store/features/telephony.feature';
import { ACTIVE_PHASES, useCall, type ActiveCall } from './call-context';
import { KEYS, clock, endedSummary, rateLabel } from './call-format';

// Above dialog overlays, and clickable while a modal dialog is open (Radix
// turns pointer events off outside it).
const CARD =
  'pointer-events-auto fixed right-4 bottom-4 z-[60] w-80 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-xl';

/** The floating call bar, and the unsaved-call reminder when no call is on. */
export function CallBar({ organizationId }: { organizationId: string }) {
  const calling = useCall();
  const { call } = calling;
  if (call)
    return <ActiveCallCard call={call} organizationId={organizationId} />;
  return <UnsavedReminder organizationId={organizationId} />;
}

function useSeconds(since: number | undefined, running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  return since ? (now - since) / 1000 : 0;
}

function ActiveCallCard({
  call,
  organizationId,
}: {
  call: ActiveCall;
  organizationId: string;
}) {
  const { hangUp, toggleMute, sendDigits, dismiss } = useCall();
  const [keypad, setKeypad] = useState(false);
  const [dialed, setDialed] = useState('');
  const active = ACTIVE_PHASES.has(call.phase) && call.phase !== 'ending';
  const seconds = useSeconds(call.answeredAt, call.phase === 'live');

  // M mutes, K opens the keypad, Esc closes it. Hang up has no key, so a
  // stray press never ends a call.
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        target?.closest('input, textarea, select, [contenteditable="true"]')
      )
        return;
      if (event.key === 'm' || event.key === 'M') toggleMute();
      else if (event.key === 'k' || event.key === 'K') setKeypad((v) => !v);
      else if (event.key === 'Escape') setKeypad(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, toggleMute]);

  const status =
    call.phase === 'checking'
      ? 'Checking…'
      : call.phase === 'connecting'
        ? 'Calling…'
        : call.phase === 'ringing'
          ? 'Ringing…'
          : call.phase === 'live'
            ? clock(seconds)
            : call.phase === 'reconnecting'
              ? 'Reconnecting…'
              : call.phase === 'ending'
                ? 'Finishing call…'
                : call.phase === 'ended'
                  ? endedSummary(call.result)
                  : call.phase === 'blocked'
                    ? 'Not placed'
                    : "Couldn't call";

  return (
    <section
      className={CARD}
      data-call-bar
      role='region'
      aria-label='Call'
      aria-live='polite'
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className='truncate text-sm font-semibold'>
            {call.label || formatPhone(call.to)}
          </p>
          {(call.label || (active && (call.rateCredits || call.fromE164))) && (
            <p className='truncate text-xs text-muted-foreground tabular-nums'>
              {[
                call.label ? formatPhone(call.to) : null,
                active && call.rateCredits ? rateLabel(call.rateCredits) : null,
                active && call.fromE164
                  ? `from ${formatPhone(call.fromE164)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 text-xs font-medium tabular-nums',
            call.phase === 'live' ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          {status}
        </span>
      </div>

      {call.error && !active && (
        <p role='alert' className='mt-3 text-sm text-foreground'>
          {call.error}
        </p>
      )}

      {active && (
        <>
          <div className='mt-4 flex items-center justify-between gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-9 flex-1 gap-1.5'
              aria-pressed={call.muted}
              disabled={call.phase !== 'live' && call.phase !== 'ringing'}
              onClick={toggleMute}
            >
              <AppIcon
                icon={call.muted ? iconMicrophoneSlash : iconMicrophone}
                style={{ fontSize: 13 }}
              />
              {call.muted ? 'Unmute' : 'Mute'}
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-9 flex-1 gap-1.5'
              aria-pressed={keypad}
              disabled={call.phase !== 'live'}
              onClick={() => setKeypad((v) => !v)}
            >
              <AppIcon icon={iconDialpad} style={{ fontSize: 13 }} />
              Keypad
            </Button>
            <Button
              type='button'
              size='sm'
              className='h-9 flex-1 gap-1.5 bg-red-600 text-white hover:bg-red-700'
              onClick={hangUp}
            >
              <AppIcon icon={iconPhoneHangup} style={{ fontSize: 13 }} />
              Hang up
            </Button>
          </div>
          {keypad && call.phase === 'live' && (
            <div className='mt-3'>
              <p
                className='mb-2 h-5 text-center font-mono text-sm tracking-widest'
                aria-live='off'
              >
                {dialed}
              </p>
              <div className='grid grid-cols-3 gap-1.5'>
                {KEYS.map((key) => (
                  <button
                    key={key}
                    type='button'
                    className='h-9 rounded-md border border-border text-sm font-medium hover:bg-muted'
                    onClick={() => {
                      sendDigits(key);
                      setDialed((v) => (v + key).slice(-16));
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {call.phase === 'ended' && call.result && !call.result.taskId && (
        <WrapUp
          organizationId={organizationId}
          record={call.result}
          onDone={dismiss}
        />
      )}
      {call.phase === 'ended' && call.result?.taskId && (
        <div className='mt-3 flex items-center justify-between gap-2'>
          <p className='text-xs text-muted-foreground'>
            Pick the disposition in the task.
          </p>
          <Button variant='ghost' size='sm' className='h-8' onClick={dismiss}>
            Close
          </Button>
        </div>
      )}
      {(call.phase === 'blocked' ||
        call.phase === 'failed' ||
        (call.phase === 'ended' && !call.result)) && (
        <div className='mt-3 flex justify-end'>
          <Button variant='outline' size='sm' className='h-8' onClick={dismiss}>
            Close
          </Button>
        </div>
      )}
    </section>
  );
}

/** Disposition and notes for a call with no task; saved to its CRM log. */
function WrapUp({
  organizationId,
  record,
  onDone,
}: {
  organizationId: string;
  record: CallRecord;
  onDone: () => void;
}) {
  const { currentData: outcomes } = useGetCallOutcomesQuery({ organizationId });
  const [save, { isLoading }] = useWrapUpCallMutation();
  const [disposition, setDisposition] = useState('');
  const [notes, setNotes] = useState('');
  const [dnc, setDnc] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setError(undefined);
    const body = {
      organizationId,
      id: record.id,
      disposition,
      notes: notes.trim() || undefined,
      addToDoNotCall: dnc,
    };
    // The server answers call_finishing until Twilio's last callback lands.
    for (let tries = 0; tries < 4; tries += 1) {
      try {
        await save(body).unwrap();
        toast.success('Call saved');
        onDone();
        return;
      } catch (failure) {
        if (apiErrorCode(failure) === 'call_finishing' && tries < 3) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          continue;
        }
        setError(apiErrorMessage(failure, "Couldn't save the call."));
        return;
      }
    }
  };

  return (
    <div className='mt-3 space-y-3'>
      <div className='space-y-1.5'>
        <Label htmlFor='call-disposition'>Disposition</Label>
        <Select value={disposition} onValueChange={setDisposition}>
          <SelectTrigger id='call-disposition' className='h-9'>
            <SelectValue placeholder='How did it go?' />
          </SelectTrigger>
          {/* Above the call bar (z-[60]); the menu renders in a portal. */}
          <SelectContent className='z-[70]'>
            {(outcomes ?? []).map((outcome) => (
              <SelectItem key={outcome.value} value={outcome.value}>
                {outcome.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Textarea
        aria-label='Call notes'
        placeholder='Notes'
        rows={3}
        maxLength={10_000}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
      />
      <label className='flex items-center gap-2 text-xs text-muted-foreground'>
        <Checkbox
          checked={dnc}
          onCheckedChange={(value) => setDnc(value === true)}
        />
        Add this number to do-not-call
      </label>
      {error && (
        <p role='alert' className='text-xs text-destructive'>
          {error}
        </p>
      )}
      <div className='flex justify-end gap-2'>
        <Button
          variant='ghost'
          size='sm'
          className='h-8'
          disabled={isLoading}
          onClick={onDone}
        >
          Later
        </Button>
        <Button
          size='sm'
          className='h-8'
          disabled={!disposition || isLoading}
          onClick={() => void submit()}
        >
          {isLoading ? 'Saving…' : 'Save call'}
        </Button>
      </div>
    </div>
  );
}

/**
 * A call that ended without being saved (the tab closed mid-call, or "Later"),
 * from the server's list, so nothing is lost. Dismissing hides it for this
 * visit only.
 */
function UnsavedReminder({ organizationId }: { organizationId: string }) {
  const { available, review } = useCall();
  const { currentData } = useListUnsavedCallsQuery(organizationId, {
    skip: !available || !organizationId,
    refetchOnMountOrArgChange: 60,
  });
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const next = (currentData ?? []).find((record) => !hidden.has(record.id));
  if (!next) return null;
  const close = () => setHidden((current) => new Set(current).add(next.id));
  return (
    <section
      className={CARD}
      data-call-bar
      role='region'
      aria-label='Unsaved call'
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-2'>
          <AppIcon
            icon={iconPhone}
            className='shrink-0 text-muted-foreground'
            style={{ fontSize: 13 }}
          />
          <p className='truncate text-sm'>
            Unsaved call to{' '}
            <span className='tabular-nums'>{formatPhone(next.toE164)}</span>
          </p>
        </div>
        <button
          type='button'
          aria-label='Hide'
          className='text-muted-foreground hover:text-foreground'
          onClick={close}
        >
          <AppIcon icon={iconXmark} style={{ fontSize: 12 }} />
        </button>
      </div>
      <p className='mt-1 text-xs text-muted-foreground'>{endedSummary(next)}</p>
      <div className='mt-3 flex justify-end'>
        {next.taskId ? (
          <Button asChild size='sm' className='h-8' onClick={close}>
            <Link to='/tasks' search={{ task: next.taskId }}>
              Open its task
            </Link>
          </Button>
        ) : (
          <Button size='sm' className='h-8' onClick={() => review(next)}>
            Save it
          </Button>
        )}
      </div>
    </section>
  );
}
